import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  SegmentedControl,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Building2, CalendarClock, Gauge } from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useCurrentPermissionsState,
  useLocations,
  useOccupancy,
  useOccupancyProfile,
} from '@/api/queries'
import type { Location, LocationOccupancy, OccupancyProfile, OpeningHour } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { timeLabel } from '@/features/courses/week'
import { LocationOccupancyCard } from '@/features/occupancy/location-card'
import {
  currentHourOf,
  peakSlots,
  profileChoices,
  quietSlots,
  realLocationCount,
  typicalDay,
  visibleHours,
  WHOLE_ORGANIZATION,
  zonedClock,
} from '@/features/occupancy/occupancy'
import { SlotList, TodayVersusUsual, WeekHeatmap } from '@/features/occupancy/profile'
import { WEEKDAY_LABELS } from '@/features/organization/opening-hours'
import { plural } from '@/lib/format'

/**
 * L'affollamento — ACC-07.
 *
 * ## Per chi è, e perché non è la reception
 *
 * La reception risponde a «chi è dentro» con i nomi, perché al banco si fa entrare e uscire una
 * persona alla volta. Questa pagina risponde a «quanti», sede per sede, per chi decide: la
 * sicurezza — quanti posti restano —, il personale in sala, le pulizie. È lo stesso registro
 * contato invece che elencato, con lo stesso permesso (`access:read`): chi può vedere i nomi può
 * vedere il numero, e il numero è quello della reception e del varco, non una seconda stima.
 *
 * ## Il numero dice anche quanto è vero
 *
 * Quasi nessuna palestra registra le uscite: il QR si mostra entrando, e uscendo si spinge una
 * porta. Il server toglie dal conto chi è entrato da più dell'**uscita presunta** della sede, e
 * ogni riquadro dice quante uscite sono state viste davvero oggi e quante persone sono state tolte
 * per presunzione. Un numero che è una stima e non lo dice è il modo più rapido di perdere la
 * fiducia di chi lo guarda — e la reception smetterebbe di credere anche a quello giusto.
 *
 * ## La settimana tipo è una seconda domanda, con un secondo permesso
 *
 * Sotto, per chi ha `stats:read`, la settimana tipo: le persone **presenti** ora per ora delle
 * ultime settimane, e oggi accanto. Serve a mettere più gente in sala alle 19 e a pulire quando la
 * sala è vuota ma aperta, ed è un dato per decidere, non per far entrare qualcuno — per questo
 * chiede il permesso delle analisi. Le due metà hanno richieste, caricamenti ed errori separati:
 * se la seconda cade, la prima resta.
 */
export const Route = createFileRoute('/_app/affollamento/')({
  component: OccupancyPage,
})

function OccupancyPage() {
  const { permissions, isPending } = useCurrentPermissionsState()
  const canRead = permissions.has('access:read')
  const canReadStats = permissions.has('stats:read')
  const occupancy = useOccupancy(canRead)
  // Gli orari di apertura stanno sulla sede e non nella risposta dell'affollamento: servono alle
  // fasce tranquille e al tratteggio delle ore chiuse. Se questa lettura cade, le due cose
  // ripiegano sulle ore in cui di solito c'è qualcuno — e la schermata lo dice.
  const locations = useLocations()

  const header = (
    <PageHeader
      title="Affollamento"
      description="Quanta gente c’è adesso, sede per sede, e com’è di solito la settimana: per la sicurezza, il personale in sala e le pulizie."
      actions={
        occupancy.data === undefined ? undefined : (
          // L'ora del conteggio e non quella della pagina: è l'istante che il server ha contato,
          // e si rinnova da sé ogni trenta secondi.
          <p
            className="text-muted-foreground text-xs sm:text-right"
            title="Si rinnova da sé ogni trenta secondi"
          >
            Aggiornato alle {timeLabel(occupancy.data.at)}
            {occupancy.data.locations.length > 1 ? (
              <span className="block">
                In tutto {plural(occupancy.data.inside, 'persona', 'persone')}
              </span>
            ) : null}
          </p>
        )
      }
    />
  )

  if (isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-56 w-full" />
      </>
    )
  }

  if (!canRead) {
    return (
      <>
        {header}
        <EmptyState
          icon={<Gauge />}
          title="Non hai accesso all’affollamento"
          // Una spiegazione e non una pagina vuota: un elenco vuoto direbbe «non c'è nessuno».
          description="Serve il permesso «access:read», lo stesso con cui la reception vede chi è dentro. Chiedilo a un titolare."
        />
      </>
    )
  }

  const entries = occupancy.data?.locations
  const hasLocations = occupancy.data === undefined ? false : realLocationCount(occupancy.data) > 0

  return (
    <>
      {header}

      <section aria-labelledby="affollamento-adesso" className="space-y-3">
        <h2 id="affollamento-adesso" className="text-base font-semibold">
          Adesso
        </h2>
        {occupancy.isPending ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-56 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : occupancy.isError ? (
          <ErrorState
            message={errorMessage(occupancy.error)}
            requestId={occupancy.error instanceof ApiError ? occupancy.error.requestId : undefined}
            onRetry={() => void occupancy.refetch()}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(entries ?? []).map((entry) => (
              <LocationOccupancyCard
                key={entry.locationId ?? 'senza-sede'}
                entry={entry}
                hasLocations={hasLocations}
              />
            ))}
          </div>
        )}
      </section>

      {canReadStats ? (
        <ProfileSection
          entries={entries}
          occupancyFailed={occupancy.isError}
          locations={locations.data}
        />
      ) : (
        <p className="text-muted-foreground mt-6 text-sm">
          La settimana tipo — le ore di punta e quelle tranquille — richiede il permesso
          «stats:read», quello delle analisi.
        </p>
      )}

      <Alert
        variant="neutral"
        className="mt-6"
        icon={<Building2 />}
        title="Uscita presunta e app degli iscritti"
        action={
          <Button size="sm" variant="outline" asChild>
            <Link to="/organizzazione">Apri le sedi</Link>
          </Button>
        }
      >
        Dopo quanti minuti chi non registra l’uscita smette di contare, e cosa vedono gli iscritti
        nell’app, si impostano sede per sede in Organizzazione.
      </Alert>
    </>
  )
}

/**
 * La settimana tipo, con i suoi due selettori.
 *
 * Le sedi fra cui scegliere sono quelle della risposta dell'affollamento — le stesse dei riquadri
 * sopra, nello stesso ordine — e non un secondo elenco: una sede che compare là deve comparire
 * qui. Solo se quella risposta è caduta si ripiega sull'elenco delle sedi, perché una lettura
 * fallita non deve portarsi via anche questa metà della pagina.
 */
function ProfileSection({
  entries,
  occupancyFailed,
  locations,
}: {
  entries: LocationOccupancy[] | undefined
  occupancyFailed: boolean
  locations: Location[] | undefined
}) {
  const [picked, setPicked] = useState<string | null>(null)
  const [weeks, setWeeks] = useState(4)

  const source =
    entries ??
    (occupancyFailed
      ? (locations ?? []).map((location) => ({
          locationId: location.id,
          locationName: location.name,
        }))
      : undefined)
  const choices = profileChoices(source ?? [])
  // La scelta resta valida solo finché la sede è fra quelle offerte: una sede archiviata mentre la
  // pagina è aperta non deve lasciare il selettore su una voce che non c'è più.
  const selected =
    picked !== null && choices.some((choice) => choice.value === picked)
      ? picked
      : (choices[0]?.value ?? WHOLE_ORGANIZATION)
  const locationId = selected === WHOLE_ORGANIZATION ? null : selected
  const profile = useOccupancyProfile(locationId, weeks, source !== undefined)

  return (
    <section aria-labelledby="affollamento-settimana" className="mt-8 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl space-y-1">
          <h2 id="affollamento-settimana" className="text-base font-semibold">
            La settimana tipo
          </h2>
          <p className="text-muted-foreground text-sm">
            Persone mediamente presenti ora per ora — non gli ingressi: alle 19 ne entrano meno che
            alle 18, ma in sala ce ne sono di più. Serve a decidere il personale e le pulizie.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {choices.length > 1 ? (
            <Field label="Sede" className="w-56">
              {(props) => (
                <Select {...props} value={selected} onValueChange={setPicked} items={choices} />
              )}
            </Field>
          ) : null}
          <Field label="Settimane contate">
            {(props) => (
              <SegmentedControl
                {...props}
                value={String(weeks)}
                onValueChange={(value) => setWeeks(Number.parseInt(value, 10))}
                options={[
                  { value: '4', label: '4' },
                  { value: '8', label: '8' },
                  { value: '12', label: '12' },
                ]}
              />
            )}
          </Field>
        </div>
      </div>

      {source === undefined || profile.isPending ? (
        <Skeleton className="h-72 w-full" />
      ) : profile.isError ? (
        <ErrorState
          message={errorMessage(profile.error)}
          requestId={profile.error instanceof ApiError ? profile.error.requestId : undefined}
          onRetry={() => void profile.refetch()}
        />
      ) : (
        <ProfileBody
          profile={profile.data}
          readAt={profile.dataUpdatedAt}
          openingHours={openingHoursFor(locationId, locations)}
        />
      )}
    </section>
  )
}

/**
 * Gli orari con cui distinguere un'ora chiusa da un'ora vuota.
 *
 * Per una sede, i suoi. Per l'organizzazione intera, l'unione di quelli di tutte le sedi — è
 * aperta se lo è almeno una —, ma solo se **tutte** li dichiarano: una sede senza orari dentro
 * l'unione farebbe sembrare chiuse ore in cui quella sede è aperta. Vuoto vuol dire «non
 * dichiarati», e le fasce tranquille ripiegano sulle ore in cui di solito c'è qualcuno.
 */
function openingHoursFor(
  locationId: string | null,
  locations: Location[] | undefined,
): OpeningHour[] {
  if (locations === undefined) return []
  if (locationId !== null) {
    return locations.find((location) => location.id === locationId)?.openingHours ?? []
  }
  if (locations.length === 0 || locations.some((location) => location.openingHours.length === 0)) {
    return []
  }
  return locations.flatMap((location) => location.openingHours)
}

function ProfileBody({
  profile,
  readAt,
  openingHours,
}: {
  profile: OccupancyProfile
  /** Quando la risposta è arrivata: è l'istante in cui `today` era «oggi». */
  readAt: number
  openingHours: OpeningHour[]
}) {
  const todayValues = Array.from(
    { length: 24 },
    (_, hour) => profile.today.find((slot) => slot.hour === hour)?.average ?? null,
  )
  const hours = visibleHours([...profile.cells, ...profile.today])
  // Il periodo sotto il titolo, come nelle analisi: chi ritaglia uno screenshot deve sapere di
  // quali settimane parla, e in che fuso sono le ore.
  const period = (
    <p className="text-muted-foreground text-xs tabular-nums">
      Dal {profile.from} al {profile.to} ({plural(profile.weeks, 'settimana', 'settimane')} intere,
      oggi escluso) · ore di {profile.timeZone}
    </p>
  )

  if (hours === null) {
    // Nessuno né nelle settimane contate né oggi: la settimana tipo non ha niente da dire, e lo
    // dice. Una griglia di zeri sembrerebbe una palestra sempre chiusa.
    return (
      <>
        {period}
        <EmptyState
          icon={<CalendarClock />}
          title="Ancora nessuna storia"
          description="La settimana tipo si riempie con i passaggi registrati al varco: dopo qualche settimana di ingressi qui compaiono le ore di punta e quelle tranquille."
        />
      </>
    )
  }

  // Il giorno della settimana **nel fuso del profilo**, all'istante in cui la risposta è
  // arrivata: è quello con cui il server ha deciso che cos'è «oggi». Il giorno del browser
  // sbaglierebbe di uno, a cavallo della mezzanotte, per chi guarda da un altro fuso.
  const weekday = zonedClock(readAt, profile.timeZone).weekday
  const weekdayName = (WEEKDAY_LABELS[weekday] ?? '').toLowerCase()
  // La domenica è femminile: «il domenica tipico» si nota subito, e fa dubitare del resto.
  const sunday = weekday === 7
  const currentHour = currentHourOf(profile.today)
  const peak = profile.usualPeak?.average ?? 0
  const peaks = peakSlots(profile.cells)
  // Senza storia non ci sono ore tranquille da suggerire: sarebbero le prime ore di apertura con
  // zero persone, cioè un elenco che sembra un consiglio e non lo è.
  const quiet =
    peak > 0
      ? quietSlots(profile.cells, openingHours)
      : { slots: [], basis: openingHours.length > 0 ? 'opening_hours' : 'activity' }

  return (
    <div className="space-y-4">
      {period}

      <Card>
        <CardHeader>
          <CardTitle as="h3">Oggi e di solito</CardTitle>
          <CardDescription>
            Oggi ora per ora, accanto {sunday ? 'alla domenica tipica' : `al ${weekdayName} tipico`}{' '}
            delle ultime {plural(profile.weeks, 'settimana', 'settimane')}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TodayVersusUsual
            today={todayValues}
            typical={typicalDay(profile.cells, weekday)}
            hours={hours}
            currentHour={currentHour}
            usualLabel={`Di solito ${sunday ? 'la' : 'il'} ${weekdayName}`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h3">Settimana tipo</CardTitle>
          <CardDescription>
            Più scura, più gente: ogni casella è la media delle persone in sala in quell’ora,
            rispetto alla più piena della settimana.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {peak > 0 ? (
            <WeekHeatmap
              cells={profile.cells}
              peak={peak}
              hours={hours}
              now={currentHour === null ? null : { weekday, hour: currentHour }}
              openingHours={openingHours}
              timeZone={profile.timeZone}
            />
          ) : (
            // C'è qualcuno oggi ma nessuno nelle settimane contate: una sede appena aperta, o
            // passaggi registrati da poco. La giornata sopra si vede, la settimana tipo non c'è.
            <p className="text-muted-foreground text-sm">
              Nelle settimane contate non è passato nessuno: la settimana tipo comincia a riempirsi
              con le prossime.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-6 pt-4 md:grid-cols-2">
          <SlotList
            title="Fasce di punta — più personale in sala"
            description="Le tre ore più piene della settimana tipo."
            slots={peaks}
            emptyLabel="Nessuna ora con qualcuno in sala nelle settimane contate."
          />
          <SlotList
            title="Fasce tranquille — pulizie e manutenzione"
            description={
              quiet.basis === 'opening_hours'
                ? 'Le tre ore meno piene fra quelle di apertura: a sede chiusa non c’è nessuno a pulire.'
                : 'Orari di apertura non dichiarati: le tre ore meno piene fra quelle in cui di solito c’è qualcuno.'
            }
            slots={quiet.slots}
            emptyLabel="Nessuna ora di apertura con dati nelle settimane contate."
          />
        </CardContent>
      </Card>
    </div>
  )
}
